/**
 * Status (online/abwesend/nicht stören/unsichtbar) und eigener Status (Emoji, Text, Ablauf).
 * Speichern per PUT /presence/@me (Vertrag 4.2, dauerhaft); die Antwort wird als PRESENCE_UPDATE angewendet.
 */
import { FIXED_LIMITS, type CustomStatusDto, type PresenceStatus } from '@lagerfeuer/shared';
import { X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { STATUS_LABELS } from '../../components/Avatar';
import { EmojiField } from '../../components/EmojiField';
import { Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { presenceApi } from '../../lib/endpoints';
import { appState, useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { activeCustomStatus } from '../profile/customStatus';
import './settings.css';

export const STATUS_OPTIONS: { status: PresenceStatus; hint?: string }[] = [
  { status: 'ONLINE' },
  { status: 'IDLE' },
  { status: 'DND', hint: 'Keine Benachrichtigungstöne und Desktop-Hinweise' },
  { status: 'INVISIBLE', hint: 'Du erscheinst offline, kannst aber alles nutzen' },
];

export type Expiry = '30m' | '1h' | '4h' | 'today' | 'never';
export const EXPIRY_LABELS: Record<Expiry, string> = { '30m': '30 Minuten', '1h': '1 Stunde', '4h': '4 Stunden', today: 'Heute', never: 'Nie' };

/** Ablaufzeitpunkt (ISO) oder null für „nie". „Heute" = Ende des lokalen Tages. */
export function expiryToIso(e: Expiry, now = new Date()): string | null {
  const t = now.getTime();
  switch (e) {
    case '30m':
      return new Date(t + 30 * 60_000).toISOString();
    case '1h':
      return new Date(t + 60 * 60_000).toISOString();
    case '4h':
      return new Date(t + 4 * 60 * 60_000).toISOString();
    case 'today': {
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return end.toISOString();
    }
    default:
      return null;
  }
}

export function CustomStatusDialog({ onClose }: { onClose: () => void }) {
  const me = useAppStore((s) => s.me);
  const current = activeCustomStatus(me?.customStatus);
  const [status, setStatus] = useState<PresenceStatus>(me?.status && me.status !== 'OFFLINE' ? me.status : 'ONLINE');
  const [emoji, setEmoji] = useState(current?.emoji ?? '');
  const [text, setText] = useState(current?.text ?? '');
  const [expiry, setExpiry] = useState<Expiry>('today');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!me) return null;

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    const custom: CustomStatusDto | null = text.trim() || emoji.trim() ? { text: text.trim() || null, emoji: emoji.trim() || null, expiresAt: expiryToIso(expiry) } : null;
    const prev = { status: me.status, customStatus: me.customStatus };
    appState().update((d) => {
      if (d.me) {
        d.me.status = status;
        d.me.customStatus = custom;
      }
    });
    try {
      const presence = await presenceApi.set(status, custom);
      appState().dispatch('PRESENCE_UPDATE', presence);
      onClose();
    } catch (err) {
      appState().update((d) => {
        if (d.me) Object.assign(d.me, prev);
      });
      setError(errorMessage(err));
      toast.error('Status konnte nicht gespeichert werden', errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Status festlegen"
      onClose={onClose}
      testId="custom-status-dialog"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="custom-status-cancel">
            Abbrechen
          </button>
          <button type="submit" form="custom-status-form" className="btn btn-primary" disabled={busy} data-testid="custom-status-save">
            Speichern
          </button>
        </>
      }
    >
      <form id="custom-status-form" className="stack" onSubmit={save}>
        <fieldset className="lf-fieldset">
          <legend className="label">Status</legend>
          <div className="lf-status-options" role="radiogroup" aria-label="Status">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.status}
                type="button"
                role="radio"
                aria-checked={status === o.status}
                className="lf-status-option"
                onClick={() => setStatus(o.status)}
                data-testid={`status-option-${o.status}`}
              >
                <span className="status-dot" data-status={o.status === 'INVISIBLE' ? 'OFFLINE' : o.status} aria-hidden="true" />
                <span className="lf-status-option-text">
                  <span>{STATUS_LABELS[o.status]}</span>
                  {o.hint && <span className="small faint">{o.hint}</span>}
                </span>
              </button>
            ))}
          </div>
        </fieldset>
        <div className="field">
          <label htmlFor="cs-text">Eigener Status</label>
          <div className="lf-custom-status-row">
            <EmojiField value={emoji || null} onChange={(v) => setEmoji(v ?? '')} label="Status-Emoji" testId="custom-status-emoji" />
            <input
              id="cs-text"
              className="input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={FIXED_LIMITS.customStatusMax}
              placeholder="Was machst du gerade?"
              data-testid="custom-status-text"
            />
            {(text || emoji) && (
              <button
                type="button"
                className="icon-btn"
                aria-label="Eigenen Status leeren"
                onClick={() => {
                  setText('');
                  setEmoji('');
                }}
                data-testid="custom-status-clear"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        <div className="field">
          <label htmlFor="cs-expiry">Löschen nach</label>
          <select id="cs-expiry" className="select" value={expiry} onChange={(e) => setExpiry(e.target.value as Expiry)} data-testid="custom-status-expiry" disabled={!text && !emoji}>
            {(Object.keys(EXPIRY_LABELS) as Expiry[]).map((k) => (
              <option key={k} value={k}>
                {EXPIRY_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <p className="error-text" role="alert" data-testid="custom-status-error">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
